import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function ChinaButlerDemandSurgePage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: '中国富裕層のバトラー需要急増' }
  ];

  const article = {
    category: '新興市場・成長トレンド',
    date: '2024年12月4日',
    title: '中国富裕層のバトラー需要急増',
    subtitle: 'アジア最大市場における執事サービスの急成長',
    heroImage: '/images/generated/jba-fs-060-news_china-butler-demand-surge.webp',
    lead: '中国の富裕層の間でプライベートバトラーサービスの需要が急増しており、約1,000人の執事を管理する企業も登場しています。家事サービス産業全体の市場規模は1兆元を超え、高品質な専門サービスへの需要が著しく増加しています。高等教育機関での家政学専攻設立も相次ぎ、執事という職業の社会的地位向上が進んでいます。',
    sections: [
      {
        heading: '市場概況と事業規模',
        image: '/images/generated/jba-fs-061-news_china-butler-demand-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: '専門的な訓練を受けるプライベートバトラー候補者たちのイメージ',
        paragraphs: [
          '中国の富裕層の間でプライベートバトラーサービスの需要が急増しています。北京を拠点とする訓練企業Meiyinghui（美盈会）によると、執事サービスは2015年以降、中国で一般的になってきており、現在では約1,000人の執事を管理する規模まで成長しています。',
          'プライベートバトラーは通常3〜5ヶ月の訓練セッションを経て業務を開始し、候補者の70%が女性となっています。COVID-19パンデミックの影響を受けることなく、過去2〜3年間は供給が需要に追いついていない状況が続いています。'
        ]
      },
      {
        heading: '雇用形態と長期的関係性',
        image: '/images/generated/jba-fs-062-news_china-butler-demand-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: '長期的な信頼関係のもとで富裕層家庭を支えるプライベートバトラーのイメージ',
        paragraphs: [
          'プライベートバトラーは通常、雇用主のために長期的に働く傾向があり、同じ家族のために最低でも2〜3年働くことが一般的です。この長期的な雇用関係は、バトラーサービスの本質である信頼関係の構築と継続的なサービス向上を可能にしています。',
          '2年間プライベートバトラートレーナーを務めるZeng Jingjing氏は、メルボルンで学びビジネス英語を専攻した経歴を持ち、「これは朝日産業です」とコメントしており、業界の将来性に対する強い確信を示しています。'
        ]
      },
      {
        heading: '家事サービス産業全体の成長',
        image: '/images/generated/jba-fs-063-news_china-butler-demand-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: '市場規模が1兆元を超える中国の家事サービス産業のイメージ',
        paragraphs: [
          '国家発展改革委員会（NDRC）の発表によると、中国の家事サービス産業の市場規模は1兆元を超え、従業員数は3,000万人を超えています。しかし、高品質な家事サービス人材の不足が特に顕著で、推定2,000万人以上の家事労働者が不足している状況です。',
          '家事サービスの需要はもはや日常の雑用に限定されておらず、オンラインショッピングと食品配達の普及により消費者の利便性が向上した結果、より高レベルの専門サービスへの需要が顕著になっています。特にペットケア、医療サービス、文化的生活計画サービスの分野で需要が著しく増加しています。'
        ]
      },
      {
        heading: '高等教育機関の参入と職業の社会的地位向上',
        image: '/images/generated/jba-fs-064-news_china-butler-demand-surge-v3.webp', aspect: 'aspect-[4/3]',
        alt: '家政学専攻を設立し職業の学術的基盤を築く大学プログラムのイメージ',
        paragraphs: [
          '吉林農業大学や河北師範大学などの大学が家政学専攻を設立し、執事という職業の学術的基盤を確立しています。家政学を専攻する高学歴者は、必ずしも家事サービス要員になる必要はなく、家政マネージャー、コミュニティサービスマネージャー、プロジェクトマネージャーなどの管理職への道も開かれています。',
          '河南家事サービス協会によると、これらの管理職位の需要は市場需要の約半分を占め、家政学講師および職業大学の専門教師は市場需要の25%を占めています。これは執事関連職業の多様化と専門化を示す重要な指標です。'
        ]
      },
      {
        heading: '新しい職業の認定と市場の多様化',
        image: '/images/generated/jba-fs-065-news_china-butler-demand-surge-v4.webp', aspect: 'aspect-[3/4]',
        alt: '国家職業分類に新たに追加される専門職の認定のイメージ',
        paragraphs: [
          '人的資源社会保障部は2024年7月末、国家職業分類リストに約20の新しい職業を追加しました。これには、ライブストリーミングアンカー、クラウドネットワークインテリジェント運用保守要員、ユーザー成長運用スペシャリスト、文化・創造的製品企画運用スペシャリストなどが含まれています。',
          '特に注目すべきは「レジャーキャンプサイトバトラー」という新職業の認定で、これは執事という概念が従来の家庭内サービスを超えて、レジャー・観光業界にまで拡大していることを示しています。新たに追加された職業の半分以上は新しい質的生産力と密接に関連しており、人々の生活における多様化と新しい需要を反映しています。'
        ]
      }
    ],
    comment: '中国におけるバトラーサービス需要の急成長は、アジア最大市場における執事サービスの将来性を明確に示す重要な市場シグナルとして、弊協会としても深く注目しております。特に、家事サービスから専門的バトラーサービスへの市場進化と、高等教育機関での家政学専攻設立が示す職業の社会的地位向上は、執事という職業の発展可能性を示すものです。中国の富裕層増加とバトラーサービス需要の相関関係は、日本における執事サービス市場との比較可能性を示唆しており、アジア地域全体での執事サービス認知度向上の可能性を感じております。このような国際的な市場動向を踏まえ、私どもとしても、アジア地域における執事サービスの標準化と品質向上に向けて、積極的に取り組んでまいります。',
    source: { name: 'China Daily', url: 'https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html', access: '2025年10月' },
    related: [
      { to: '/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-066-news_china-butler-demand-surge-v4.webp', aspect: 'aspect-[3/4]', alt: 'AIホスピタリティ革命：5つの変革の関連記事', title: 'AIホスピタリティ革命：5つの変革', desc: '人工知能が切り開く執事サービスの未来' },
      { to: '/news/hong-kong-butler-ai-profitability', image: '/images/generated/jba-fs-067-news_china-butler-demand-surge-v4.webp', aspect: 'aspect-[3/4]', alt: '香港Butler AI 黒字化達成の関連記事', title: '香港Butler AI 黒字化達成', desc: 'AI搭載プロパティ管理プラットフォームの成功' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'China Daily', link: false },
    { label: '元記事URL', value: 'https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html', link: true },
    { label: '給与・続報', value: 'https://www.chinadaily.com.cn/a/202506/02/WS683cea61a310a04af22c2b79.html', link: true },
    { label: '富裕層データ', value: 'https://www.statista.com/topics/5788/millionaires-in-china/', link: true },
    { label: '高級住宅市場', value: 'https://daxueconsulting.com/luxury-home-market-in-china/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-market', label: '市場概況と事業規模' },
    { id: 's-employment', label: '雇用形態と長期的関係性' },
    { id: 's-salary', label: '執事の給与水準と担い手' },
    { id: 's-industry', label: '家事サービス産業全体の成長' },
    { id: 's-education', label: '高等教育機関の参入と職業の社会的地位向上' },
    { id: 's-wealth', label: '需要を牽引する富裕層と高級住宅市場' },
    { id: 's-newjobs', label: '新しい職業の認定と市場の多様化' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '中国の富裕層でプライベートバトラー需要が急増し、約1,000人の執事を管理する企業も登場した',
                '家事サービス産業全体の市場規模は1兆元を超え、平均年収は約40万元・担い手の70%が女性',
                '大学の家政学専攻設立や新職業認定が進み、執事という職業の社会的地位が向上している'
              ]}
            />

            <SectionHeading id="s-market" en="Market Overview">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/china-chinadaily.jpg"
              alt="中国富裕層の執事需要を伝えるChina Dailyの記事のスクリーンショット"
              media="China Daily"
              url="https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="これは朝日産業です。" cite="Zeng Jingjing / プライベートバトラートレーナー" />
            </div>

            <SectionHeading id="s-employment" en="Employment">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-salary" en="Pay & People">執事の給与水準と担い手</SectionHeading>
            <div className="space-y-6">
              <p className={P}>China Dailyの続報によると、プライベート執事の平均年収は約<Stat>40万元</Stat>（約5万5,000ドル）に達し、経験やスキルに応じて20万元から80万元の幅があります。需要が供給を上回るなかで、条件は担い手に有利な方向へ動いています。</p>
              <p className={P}>担い手の<Stat>70%</Stat>は女性で、大学卒業者や海外留学経験者の参入も増えています。北京だけでも執事を管理・派遣する企業が少なくとも5社存在し、主要都市だけでなく二・三線都市へと需要が広がっています。</p>
            </div>

            <SectionHeading id="s-industry" en="Industry">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-education" en="Education">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-wealth" en="Wealth Base">需要を牽引する富裕層と高級住宅市場</SectionHeading>
            <div className="space-y-6">
              <p className={P}>需要の背景には、中国の富裕層の厚みがあります。ドル建ての百万長者は米国に次ぐ規模で、推定約<Stat>630万人</Stat>とされ、若年化・海外経験の広がりが執事サービスの受容を後押ししています。</p>
              <p className={P}>高級住宅市場も需要を支えています。価格1,000万元を超える高級住宅は、2023年にトップ10都市で約<Stat>52,000戸</Stat>が販売されたと推計され、こうした富裕層の資産形成が専門的な家庭内サービスへの需要へと直結しています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/china-wikipedia.jpg"
              alt="執事という職業の歴史と役割を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Butler）"
              url="https://en.wikipedia.org/wiki/Butler"
            />

            <SectionHeading id="s-newjobs" en="New Professions">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">平均年収</dt><dd className="text-[#333] mt-0.5">約40万元（約5.5万ドル）</dd></div>
                  <div><dt className="text-[#8f7a56]">女性比率</dt><dd className="text-[#333] mt-0.5">約70%</dd></div>
                  <div><dt className="text-[#8f7a56]">家事産業市場</dt><dd className="text-[#333] mt-0.5">1兆元超</dd></div>
                  <div><dt className="text-[#8f7a56]">ドル建て富裕層</dt><dd className="text-[#333] mt-0.5">約630万人</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
